import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

// Core interfaces
export interface User {
  id: string;
  email: string;
  name: string;
  archived: boolean;
  tagId: string;
  tagName: string;
  lastSeen: string | null;
  interactiveTrackingMode: 'manual' | 'auto';
  trackingMode: 'interactive' | 'passive';
  everHadBreak: boolean;
  oauth: {
    enabled: boolean;
  };
  isInteractiveAutoTracking: boolean;
  hasPassword: boolean;
  lastSeenGlobal: string | null;
}

export interface TimeTrackingData {
  userId: string;
  total: number;
  productive: number;
  unproductive: number;
  neutral: number;
  meeting: number;
  idle: number;
  manual: number;
  mobile: number;
  computer: number;
  offcomputer: number;
  paidBreak: number;
  date: string;
}

export interface ProductivityMetrics {
  userId: string;
  productiveRatio: number;
  unproductiveRatio: number;
  neutralRatio: number;
  meetingRatio: number;
  idleRatio: number;
  manualRatio: number;
  mobileRatio: number;
  computerRatio: number;
  offcomputerRatio: number;
  paidBreakRatio: number;
}

@Injectable({
  providedIn: 'root',
})
export class MockDataStore {
  // Singleton instance
  private static instance: MockDataStore | null = null;

  // Core data stores
  private readonly users: Map<string, User> = new Map();
  private readonly timeTracking: Map<string, TimeTrackingData[]> = new Map();
  private readonly productivityMetrics: Map<string, ProductivityMetrics> = new Map();

  // Deterministic ratios
  private readonly standardRatios = {
    productive: 0.75,
    unproductive: 0.15,
    neutral: 0.10,
    meeting: 0.20,
    computer: 0.65,
    mobile: 0.10,
    manual: 0.15,
    offcomputer: 0.10,
    paidBreak: 0.08,
    idle: 0.05,
  };

  // Base work times in seconds
  private readonly userBaseTimes: Record<string, number> = {
    'user-1': 28800, // 8 hours
    'user-2': 25200, // 7 hours
    'user-3': 32400,  // 9 hours
    'user-4': 27000, // 7.5 hours
    'user-5': 30600, // 8.5 hours
    'user-silent-1': 28800, // 8 hours — automatic/silent tracking demo user
  };

  constructor() {
    // Singleton pattern
    if (MockDataStore.instance) {
      return MockDataStore.instance;
    }
    MockDataStore.instance = this;
    this.initializeMockData();
  }

  // Static method to get instance
  public static getInstance(): MockDataStore {
    if (!MockDataStore.instance) {
      MockDataStore.instance = new MockDataStore();
    }
    return MockDataStore.instance;
  }

  // Cleanup method to clear data and reset instance
  public static cleanup(): void {
    if (MockDataStore.instance) {
      MockDataStore.instance.clearData();
      MockDataStore.instance = null;
    }
  }

  // Instance cleanup method for when service is destroyed
  public cleanup(): void {
    this.clearData();
  }

  private clearData(): void {
    this.users.clear();
    this.timeTracking.clear();
    this.productivityMetrics.clear();
  }

  private initializeMockData(): void {
    this.initializeUsers();
    this.initializeProductivityMetrics();
    this.initializeTimeTracking();
  }

  private initializeUsers(): void {
    const mockUsers: User[] = [
      {
        id: 'user-1',
        email: 'john.doe@domain.dev',
        name: 'John Doe',
        archived: false,
        tagId: 'tag-1',
        tagName: 'Engineering',
        lastSeen: null,
        interactiveTrackingMode: 'manual',
        trackingMode: 'interactive',
        everHadBreak: false,
        oauth: { enabled: false },
        isInteractiveAutoTracking: false,
        hasPassword: false,
        lastSeenGlobal: null,
      },
      {
        id: 'user-2',
        email: 'jane.smith@domain.dev',
        name: 'Jane Smith',
        archived: false,
        tagId: 'tag-2',
        tagName: 'Marketing',
        lastSeen: null,
        interactiveTrackingMode: 'manual',
        trackingMode: 'interactive',
        everHadBreak: false,
        oauth: { enabled: false },
        isInteractiveAutoTracking: false,
        hasPassword: false,
        lastSeenGlobal: null,
      },
      {
        id: 'user-3',
        email: 'alex.johnson@domain.dev',
        name: 'Alex Johnson',
        archived: false,
        tagId: 'tag-1',
        tagName: 'Engineering',
        lastSeen: null,
        interactiveTrackingMode: 'manual',
        trackingMode: 'interactive',
        everHadBreak: false,
        oauth: { enabled: false },
        isInteractiveAutoTracking: false,
        hasPassword: false,
        lastSeenGlobal: null,
      },
      {
        id: 'user-4',
        email: 'sarah.wilson@domain.dev',
        name: 'Sarah Wilson',
        archived: false,
        tagId: 'tag-3',
        tagName: 'Design',
        lastSeen: null,
        interactiveTrackingMode: 'manual',
        trackingMode: 'interactive',
        everHadBreak: false,
        oauth: { enabled: false },
        isInteractiveAutoTracking: false,
        hasPassword: false,
        lastSeenGlobal: null,
      },
      {
        id: 'user-5',
        email: 'michael.brown@domain.dev',
        name: 'Michael Brown',
        archived: false,
        tagId: 'tag-2',
        tagName: 'Marketing',
        lastSeen: null,
        interactiveTrackingMode: 'manual',
        trackingMode: 'interactive',
        everHadBreak: false,
        oauth: { enabled: false },
        isInteractiveAutoTracking: false,
        hasPassword: false,
        lastSeenGlobal: null,
      },
      {
        id: 'user-silent-1',
        email: 'chris.martinez@domain.dev',
        name: 'Chris Martinez',
        archived: false,
        tagId: 'tag-1',
        tagName: 'Engineering',
        lastSeen: null,
        interactiveTrackingMode: 'manual',
        trackingMode: 'passive',
        everHadBreak: false,
        oauth: { enabled: false },
        isInteractiveAutoTracking: false,
        hasPassword: false,
        lastSeenGlobal: null,
      },
    ];

    mockUsers.forEach(user => this.users.set(user.id, user));
  }

  private initializeTimeTracking(): void {
    const today = DateTime.now();
    const daysToGenerate = 7;

    this.users.forEach(user => {
      const userTracking: TimeTrackingData[] = [];
      const baseTime = this.userBaseTimes[user.id];

      for (let i = 0; i < daysToGenerate; i++) {
        const date = today.minus({ days: i });
        const trackingData = this.generateTimeTrackingData(user.id, baseTime, date);
        userTracking.push(trackingData);
      }

      this.timeTracking.set(user.id, userTracking);
    });
  }

  private initializeProductivityMetrics(): void {
    this.users.forEach(user => {
      const metrics: ProductivityMetrics = {
        userId: user.id,
        productiveRatio: this.standardRatios.productive,
        unproductiveRatio: this.standardRatios.unproductive,
        neutralRatio: this.standardRatios.neutral,
        meetingRatio: this.standardRatios.meeting,
        idleRatio: this.standardRatios.idle,
        manualRatio: this.standardRatios.manual,
        mobileRatio: this.standardRatios.mobile,
        computerRatio: this.standardRatios.computer,
        offcomputerRatio: this.standardRatios.offcomputer,
        paidBreakRatio: this.standardRatios.paidBreak,
      };

      this.productivityMetrics.set(user.id, metrics);
    });
  }

  private generateTimeTrackingData(userId: string, baseTime: number, date: DateTime): TimeTrackingData {
    const metrics = this.productivityMetrics.get(userId);

    return {
      userId,
      total: baseTime,
      productive: Math.round(baseTime * metrics.productiveRatio),
      unproductive: Math.round(baseTime * metrics.unproductiveRatio),
      neutral: Math.round(baseTime * metrics.neutralRatio),
      meeting: Math.round(baseTime * metrics.meetingRatio),
      idle: Math.round(baseTime * metrics.idleRatio),
      manual: Math.round(baseTime * metrics.manualRatio),
      mobile: Math.round(baseTime * metrics.mobileRatio),
      computer: Math.round(baseTime * metrics.computerRatio),
      offcomputer: Math.round(baseTime * metrics.offcomputerRatio),
      paidBreak: Math.round(baseTime * metrics.paidBreakRatio),
      date: date.toISODate(),
    };
  }

  // Public accessor methods
  public getUser(userId: string): User | undefined {
    return this.users.get(userId);
  }

  public getAllUsers(): User[] {
    return Array.from(this.users.values());
  }

  public getTimeTracking(userId: string): TimeTrackingData[] {
    return this.timeTracking.get(userId) || [];
  }

  public getProductivityMetrics(userId: string): ProductivityMetrics | undefined {
    return this.productivityMetrics.get(userId);
  }

  public getStandardRatios(): typeof this.standardRatios {
    return { ...this.standardRatios };
  }

  public getBaseTime(userId: string): number {
    return this.userBaseTimes[userId] || 28800; // Default to 8 hours if not found
  }
}
